import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import { useField } from "formik";
import { useSnackbar } from "notistack";
import { Fragment, useState } from "react";
import { useTimer } from "react-timer-hook";
import {
  validateClientEmail,
  validateClientPhone,
  validateEmail,
  validatePhone,
  validateUserEmail,
  validateUserPhone,
} from "Services/api/otp/otp";
import FormikSubmitButton from "Shared/FormikSubmitButton/FormikSubmitButton";
import { getFullPhoneFormat } from "Utils/getFullPhoneFormat";
import { isTesting } from "Utils/isTesting";
import * as Yup from "yup";
import FormikOtpCodeField from "./FormikOtpCodeField";

export const otpCodeSchema = Yup.string().required("Codigo Requerido").length(6, "Codigo incorrecto");

type Channel = "" | "sms" | "whatsapp" | "call" | "email";

export function OtpValidation({
  loginData,
  submitDisplay = "Siguiente",
  onBack,
  additionalFormNode,
}: {
  loginData?: {
    uid: string;
    token: string;
  };
  submitDisplay?: string;
  onBack?: () => void;
  additionalFormNode?: React.ReactNode;
}) {
  const [, , { setValue: setFieldChannel }] = useField<Channel>("channel");
  const { enqueueSnackbar } = useSnackbar();
  const [to, setTo] = useState("");
  const [channel, setChannel] = useState<Channel | "loading">("");
  const isEmpty = channel == "";
  const isLoading = channel == "loading";
  const isEmail = channel === "email";
  const isSms = channel === "sms";
  const isWhatsApp = channel === "whatsapp";
  const isCall = channel === "call";

  return (
    <Fragment>
      <Stack spacing={1}>
        {isEmpty && (
          <Alert severity="info" color="success" variant="outlined">
            Para continuar debe de confirmar un código enviado a su correo electrónico o teléfono móvil.
          </Alert>
        )}
        <label>{"Enviar código de confirmación:"}</label>
        <Stack direction={"row"} spacing={1}>
          <Button onClick={() => sendEmailOtp()} variant="outlined" fullWidth disabled={isEmail}>
            Correo
          </Button>
          <Button onClick={() => sendPhoneOtp("sms")} variant="outlined" fullWidth disabled={isSms}>
            SMS
          </Button>
          <Button onClick={() => sendPhoneOtp("whatsapp")} variant="outlined" fullWidth disabled={isWhatsApp}>
            WhatsApp
          </Button>
          <Button onClick={() => sendPhoneOtp("call")} variant="outlined" fullWidth disabled={isCall}>
            Llamada
          </Button>
        </Stack>
      </Stack>

      {isLoading && (
        <Stack spacing={2} alignItems="center">
          <CircularProgress color="inherit" />
          <div>Enviando código...</div>
        </Stack>
      )}
      {isEmail && <OtpValidationFormFields channel={channel} channelDisplay="correo" to={to} expiryTime={600} />}
      {(isSms || isWhatsApp || isCall) && (
        <OtpValidationFormFields channel={channel} channelDisplay="teléfono movil" to={to} expiryTime={600} />
      )}
      {additionalFormNode}
      <Stack direction="row" width="100%" spacing={2}>
        {onBack && (
          <Button fullWidth variant="text" sx={{ justifyContent: "flex-start" }} onClick={onBack}>
            Atras
          </Button>
        )}
        <FormikSubmitButton
          fullWidth
          variant="contained"
          sx={{ justifyContent: onBack ? "flex-end" : "" }}
          disabled={isEmpty || isLoading}
        >
          {submitDisplay}
        </FormikSubmitButton>
      </Stack>
    </Fragment>
  );

  function sendEmailOtp() {
    setChannel("loading");
    const request = loginData ? validateClientEmail(loginData.uid, loginData.token) : validateUserEmail();
    void request
      .then(({ data: { to } }) => {
        setTo(to);
        setChannel("email");
        setFieldChannel("email");
      })
      .catch((error) => {
        setChannel("");
        setFieldChannel("");
        enqueueSnackbar("Ha ocurrido un error", { variant: "error" });
        console.error(error);
        throw error;
      });
  }

  function sendPhoneOtp(channel: Channel) {
    setChannel("loading");
    const request = loginData
      ? validateClientPhone(channel, loginData.uid, loginData.token)
      : validateUserPhone(channel);
    void request
      .then(({ data: { to } }) => {
        setTo(to);
        setChannel(channel);
        setFieldChannel(channel);
      })
      .catch((error) => {
        setChannel("");
        setFieldChannel("");
        enqueueSnackbar("Ha ocurrido un error", { variant: "error" });
        console.error(error);
        throw error;
      });
  }
}

const VerificationTitle = styled(Typography)(() => ({
  color: "#7C7C7C",
  fontWeight: 700,
  fontSize: "24px",
  lineHeight: "55px",
}));

const Text = styled(Typography)(() => ({
  color: "#7C7C7C",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "20px",
}));

export interface OtpProps {
  channel: "sms" | "whatsapp" | "call" | "email";
  channelDisplay: string;
  to: string;
  expiryTime: number;
}

export function OtpValidationFormFields({ channel, channelDisplay, to, expiryTime }: OtpProps) {
  const textButtonStyle = { justifyContent: "left", padding: "6px 0px;", width: "fit-content" };
  const { enqueueSnackbar } = useSnackbar();

  const expiryTimestamp = new Date();
  expiryTimestamp.setSeconds(expiryTimestamp.getSeconds() + expiryTime);
  const { seconds, minutes, restart } = useTimer({ expiryTimestamp });
  const [openDialog, setOpenDialog] = useState(false);

  async function resendCode() {
    try {
      if (channel === "email") await validateEmail(to);
      else await validatePhone(getFullPhoneFormat(to), channel);
      enqueueSnackbar("Codigo reenviado");
      const expiryTimestamp = new Date();
      expiryTimestamp.setSeconds(expiryTimestamp.getSeconds() + expiryTime);
      restart(expiryTimestamp);
    } catch (error) {
      enqueueSnackbar("Ha ocurrido un error", { variant: "error" });
      console.error(error);
    }
  }

  let resendTimer = 0;
  let disabledResend = false;

  if (!isTesting()) {
    resendTimer = 60 - (expiryTime - (minutes * 60 + seconds));
    disabledResend = resendTimer > 0;
  }

  return (
    <Fragment>
      <Text>
        Hemos enviado un código OTP a su {channelDisplay} <strong>{to}</strong>:
      </Text>
      <VerificationTitle variant="h2">Insertar código</VerificationTitle>
      <FormikOtpCodeField name="code" id="code" fullWidth variant="outlined" required />
      <Text>
        Este código estará vigente los próximos{" "}
        {`${minutes}:${seconds.toString().length === 2 ? seconds : `0${seconds}`}`} minutos
      </Text>

      <Button sx={textButtonStyle} onClick={() => void resendCode()} disabled={disabledResend}>
        Volver a enviar código {disabledResend && `(${resendTimer === 60 ? "1:00" : `0:${resendTimer}`})`}
      </Button>
      <Button sx={textButtonStyle} onClick={() => void setOpenDialog(true)}>
        No he recibido el código
      </Button>
      {openDialog ? <NoCodeDialog open={openDialog} channel={channel} onClose={() => setOpenDialog(false)} /> : null}
    </Fragment>
  );
}

interface NoCodeDialogProps {
  open: boolean;
  channel: OtpProps["channel"];
  onClose: () => void;
}

function NoCodeDialog(props: NoCodeDialogProps) {
  const { onClose, channel, open } = props;

  return (
    <Dialog onClose={onClose} open={open}>
      <DialogTitle>¿No has recibido el código?</DialogTitle>
      <DialogContent sx={{ textAlign: "start" }}>
        <p>Si no te llega el código puedes:</p>
        <ul>
          <li>
            <strong>Esperar unos minutos:</strong> A veces puede tomar algunos minutos para el código llegar a tu{" "}
            {channel === "sms" ? "dispositivo" : "correo"}.
          </li>
          {channel === "email" ? (
            <li>
              <strong>Revisar spam:</strong> Puedes intentar revisar tu correo no deseado (spam).
            </li>
          ) : null}
          <li>
            <strong>Contactarnos:</strong> Si aún no recibes el código puedes escribirnos en{" "}
            <Link target="_blank" rel="noopener" href="https://coopbarcelona.com/contactanos/">
              nuestra pagina de contacto
            </Link>{" "}
            y con gusto te ayudaremos.
          </li>
        </ul>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
